import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Film, Loader2, AlertTriangle, Scissors, Download } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import {
  type Scene,
  type Clip,
  type RenderJob,
  type Trim,
  listClips,
  renderProject,
  getLatestRender,
  renderVideoUrl,
  renderDownloadUrl,
} from "@/lib/api";
import { MONTAGGIO } from "@/constants/testIds";
import { translateError } from "@/lib/errors";

const STATUS_KEY: Record<string, string> = {
  PENDING: "montaggio.statusPending",
  GENERATING: "montaggio.statusGenerating",
  READY: "montaggio.statusReady",
  FAILED: "montaggio.statusFailed",
  STALLED: "montaggio.statusStalled",
};

const fmtDur = (v?: number | null) =>
  v == null ? "—" : `${v.toFixed(1)}s`;

// Slugify the project title into a filesystem-safe .mp4 filename (mirrors backend).
const safeFilename = (title: string) => {
  const base = (title || "")
    .trim()
    .toLowerCase()
    .replace(/[àáâä]/g, "a")
    .replace(/[èéêë]/g, "e")
    .replace(/[ìíîï]/g, "i")
    .replace(/[òóôö]/g, "o")
    .replace(/[ùúûü]/g, "u")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${(base || "video").slice(0, 80)}.mp4`;
};

export function Montaggio({
  projectId,
  projectTitle,
  scenes,
}: {
  projectId: string;
  projectTitle: string;
  scenes: Scene[];
}) {
  const { t } = useTranslation();
  const [readyClips, setReadyClips] = useState<Record<string, Clip>>({});
  const [loaded, setLoaded] = useState(false);
  const [render, setRender] = useState<RenderJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [trims, setTrims] = useState<Record<string, Trim>>({});

  const loadClips = useCallback(async () => {
    const entries = await Promise.all(
      scenes.map(async (s) => {
        const clips = await listClips(s.id).catch(() => [] as Clip[]);
        const ready = [...clips].reverse().find((c) => c.status === "READY");
        return [s.id, ready] as const;
      }),
    );
    const map: Record<string, Clip> = {};
    for (const [sid, clip] of entries) if (clip) map[sid] = clip;
    setReadyClips(map);
    setLoaded(true);
  }, [scenes]);

  useEffect(() => {
    loadClips();
    getLatestRender(projectId).then(setRender).catch(() => {});
  }, [loadClips, projectId]);

  const includedScenes = scenes.filter((s) => readyClips[s.id]);
  const excludedScenes = scenes.filter((s) => !readyClips[s.id]);

  const setTrim = (sid: string, key: "start" | "end", raw: string) => {
    setTrims((prev) => {
      const next = { ...prev };
      const t: Trim = { ...(next[sid] || {}) };
      if (raw.trim() === "") {
        delete (t as any)[key];
      } else {
        const num = Number(raw);
        (t as any)[key] = Number.isFinite(num) && num >= 0 ? num : undefined;
      }
      if (t.start == null && t.end == null) delete next[sid];
      else next[sid] = t;
      return next;
    });
  };

  const doRender = async () => {
    setBusy(true);
    try {
      // Only send trims for scenes that will actually be included.
      const payload: Record<string, Trim> = {};
      for (const s of includedScenes) if (trims[s.id]) payload[s.id] = trims[s.id];
      const job = await renderProject(projectId, payload);
      setRender(job);
      if (job.status === "READY") {
        toast.success(t("montaggio.previewGenerated"));
      } else if (job.status === "FAILED") {
        toast.error(translateError(job.error_message, "montaggio.statusFailed"));
      } else {
        toast.message(STATUS_KEY[job.status] ? t(STATUS_KEY[job.status]) : job.status);
      }
    } catch (e: any) {
      toast.error(translateError(e?.response?.data?.detail, "montaggio.renderFailed"));
    } finally {
      setBusy(false);
    }
  };

  const canRender = includedScenes.length > 0 && !busy;

  const isDownloadable =
    render?.status === "READY" && !!render.output_storage_path;

  const doDownload = async () => {
    if (!render) return;
    setDownloading(true);
    try {
      const resp = await fetch(renderDownloadUrl(render.id));
      if (!resp.ok) {
        toast.error(t("montaggio.videoUnavailable"));
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = safeFilename(projectTitle);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("montaggio.videoUnavailable"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section data-testid={MONTAGGIO.section} className="mt-12">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-semibold uppercase tracking-wide text-foreground">
          {t("montaggio.title")}
        </h2>
        <Button
          data-testid={MONTAGGIO.renderBtn}
          onClick={doRender}
          disabled={!canRender}
          variant="secondary"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Film className="h-4 w-4" />
          )}
          {t("montaggio.generatePreview")}
        </Button>
      </div>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">{t("montaggio.loadingClips")}</p>
      ) : includedScenes.length === 0 ? (
        <Card data-testid={MONTAGGIO.empty}>
          <p className="text-sm text-muted-foreground">
            {t("montaggio.noClips")}
          </p>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          {/* Left: player + status */}
          <div className="space-y-4">
            <Card className="overflow-hidden p-0">
              <div className="relative mx-auto aspect-[9/16] max-h-[70vh] w-full max-w-sm bg-black">
                {render?.status === "READY" && render.output_storage_path ? (
                  <video
                    key={render.id}
                    data-testid={MONTAGGIO.video}
                    src={`${renderVideoUrl(render.id)}?v=${render.id}`}
                    controls
                    playsInline
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Film className="h-10 w-10 opacity-30" />
                    <p className="text-xs uppercase tracking-widest">
                      {t("montaggio.noMontage")}
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {render && (
              <div
                data-testid={MONTAGGIO.status}
                className="flex flex-wrap items-center gap-3 rounded-2xl glass-panel px-4 py-3 text-sm"
              >
                <span
                  className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    render.status === "READY"
                      ? "border-[#3DDC84]/40 bg-[#3DDC84]/15 text-[#3DDC84]"
                      : render.status === "FAILED"
                        ? "border-[#FF5C6C]/40 bg-[#FF5C6C]/15 text-[#FF5C6C]"
                        : render.status === "GENERATING"
                          ? "border-[#2E7BFF]/40 bg-[#2E7BFF]/15 text-[#2E7BFF]"
                          : "border-[#FFB020]/40 bg-[#FFB020]/15 text-[#FFB020]"
                  }`}
                >
                  {STATUS_KEY[render.status] ? t(STATUS_KEY[render.status]) : render.status}
                </span>
                {render.status === "READY" && (
                  <span className="text-muted-foreground">
                    {t("montaggio.summary", { duration: fmtDur(render.duration_seconds), count: render.included_scene_ids.length })}
                  </span>
                )}
                {render.error_message && (
                  <span className="text-[#FF5C6C]">{translateError(render.error_message)}</span>
                )}
              </div>
            )}

            {isDownloadable && (
              <Button
                data-testid={MONTAGGIO.downloadBtn}
                onClick={doDownload}
                disabled={downloading}
                variant="primary"
                className="w-full sm:w-auto"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {t("common.downloadMp4")}
              </Button>
            )}

            {excludedScenes.length > 0 && (
              <div
                data-testid={MONTAGGIO.excluded}
                className="flex items-start gap-2 rounded-lg border border-[#FFB020]/30 bg-[#FFB020]/5 px-4 py-3 text-sm text-[#FFB020]"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t("montaggio.excluded", { count: excludedScenes.length })}{" "}
                  <span className="text-foreground/80">
                    {excludedScenes.map((s) => s.title).join(", ")}
                  </span>
                  . {t("montaggio.excludedTail")}
                </span>
              </div>
            )}
          </div>

          {/* Right: included scenes + trim */}
          <div className="space-y-3">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Scissors className="h-3.5 w-3.5" /> {t("montaggio.includedTrim")}
            </p>
            {includedScenes.map((s, idx) => {
              const clip = readyClips[s.id];
              const tr = trims[s.id] || {};
              return (
                <div
                  key={s.id}
                  data-testid={MONTAGGIO.sceneRow}
                  className="rounded-2xl glass-panel p-3"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="font-display text-xs font-bold text-[#2E7BFF]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="line-clamp-1 text-sm text-foreground">
                      {s.title}
                    </span>
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {clip.duration_seconds
                        ? `${Math.round(clip.duration_seconds)}s`
                        : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("montaggio.start")}
                    </label>
                    <input
                      data-testid={MONTAGGIO.trimStart}
                      type="number"
                      min={0}
                      step={0.1}
                      value={tr.start ?? ""}
                      onChange={(e) => setTrim(s.id, "start", e.target.value)}
                      placeholder="0"
                      className="h-8 w-20 field-premium !rounded-md px-2 text-xs text-foreground outline-none focus:border-[#2E7BFF]/60"
                    />
                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {t("montaggio.end")}
                    </label>
                    <input
                      data-testid={MONTAGGIO.trimEnd}
                      type="number"
                      min={0}
                      step={0.1}
                      value={tr.end ?? ""}
                      onChange={(e) => setTrim(s.id, "end", e.target.value)}
                      placeholder={
                        clip.duration_seconds
                          ? String(Math.round(clip.duration_seconds))
                          : t("montaggio.endPlaceholder")
                      }
                      className="h-8 w-20 field-premium !rounded-md px-2 text-xs text-foreground outline-none focus:border-[#2E7BFF]/60"
                    />
                  </div>
                </div>
              );
            })}
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {t("montaggio.trimNote")}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
